import React, { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { Card, Button, Row, Col, Spinner } from 'react-bootstrap';
import { FaCalendarTimes } from 'react-icons/fa';

const PatientDashboard = () => {
    const { userData } = useOutletContext();
    const [appointments, setAppointments] = useState([]);
    const [latestReport, setLatestReport] = useState(null);
    const [latestPrescription, setLatestPrescription] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (userData?.uid) {
            fetchDashboardData(userData.uid);
        }
    }, [userData]);

    const fetchDashboardData = async (userId) => {
        try {
            setLoading(true);

            const apptQuery = query(collection(db, 'appointments'), where('bookedBy', '==', userId));
            const apptSnap = await getDocs(apptQuery);
            const apptList = apptSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            setAppointments(apptList);

            const upcoming = apptList
                .filter(a => a.status === 'Scheduled' || a.status === 'Confirmed')
                .sort((a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate));

            if (upcoming.length > 0) {
                const latestApptId = upcoming[0].id;

                const reportQuery = query(
                    collection(db, 'medicalReports'),
                    where('appointmentId', '==', latestApptId),
                    orderBy('date', 'desc'),
                    limit(1)
                );
                const reportSnap = await getDocs(reportQuery);
                if (!reportSnap.empty) {
                    setLatestReport(reportSnap.docs[0].data());
                }

                const prescriptionQuery = query(
                    collection(db, 'prescriptions'),
                    where('appointmentId', '==', latestApptId),
                    orderBy('date', 'desc'),
                    limit(1)
                );
                const presSnap = await getDocs(prescriptionQuery);
                if (!presSnap.empty) {
                    setLatestPrescription(presSnap.docs[0].data());
                }
            }

        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    const upcomingAppointments = appointments
        .filter(app => app.status === 'Confirmed' || app.status === 'Scheduled')
        .sort((a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate));

    const latestAppointment = upcomingAppointments[0];

    return (
        <div className="patient-dashboard-container">
            {/* Welcome Banner */}
            <div className="welcome-banner">
                <h2>👋 Welcome back, {userData?.name || 'Guest'}!</h2>
                <p>
                    Here's your personalized health dashboard. View upcoming appointments, track medical records, and manage your prescriptions all in one place.
                </p>
            </div>

            {/* Summary Widgets Row */}
            <Row>
                <Col md={4}>
                    <div className="summary-widget">
                        <div className="summary-icon blue">
                            <FaCalendarTimes />
                        </div>
                        <div className="summary-details">
                            <h3>{appointments.length}</h3>
                            <p>Total Appointments</p>
                        </div>
                    </div>
                </Col>
                <Col md={4}>
                    <div className="summary-widget">
                        <div className="summary-icon green">
                            <FaCalendarTimes />
                        </div>
                        <div className="summary-details">
                            <h3>{upcomingAppointments.length}</h3>
                            <p>Upcoming</p>
                        </div>
                    </div>
                </Col>
                <Col md={4}>
                    <div className="summary-widget">
                        <div className="summary-icon red">
                            <FaCalendarTimes />
                        </div>
                        <div className="summary-details">
                            <h3>{latestPrescription ? '1' : '0'}</h3>
                            <p>Active Prescriptions</p>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Main Content Grid */}
            <Row>
                <Col lg={12}>
                    <Card className="dashboard-card">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="mb-0">Upcoming Appointments</h4>
                                <Button variant="primary" size="sm" onClick={() => navigate('/patient/bookAppointment')}>
                                    + Book New
                                </Button>
                            </div>
                            <hr />
                            {loading ? (
                                <div className="text-center mt-3"><Spinner animation="border" variant="primary" /></div>
                            ) : !latestAppointment ? (
                                <div className="empty-state">
                                    <FaCalendarTimes className="icon upcoming" />
                                    <h5>No appointments scheduled</h5>
                                    <p>You have no upcoming appointments at the moment.</p>
                                    <Button variant="outline-primary" onClick={() => navigate('/patient/bookAppointment')} className="mt-2">
                                        Book an Appointment
                                    </Button>
                                </div>
                            ) : (
                                <div className="appointment-card d-flex justify-content-between align-items-center flex-wrap">
                                    <div>
                                        <p className="mb-1"><strong>Doctor:</strong> Dr. {latestAppointment.doctor} <span className="text-muted">({latestAppointment.specialization})</span></p>
                                        <p className="mb-1"><strong>Date & Time:</strong> {latestAppointment.appointmentDate} at {latestAppointment.appointmentTime}</p>
                                        <p className="mb-0">
                                            <strong>Status:</strong> 
                                            <span className={`badge ms-2 bg-${latestAppointment.status === 'Completed' ? 'success' : latestAppointment.status === 'Cancelled' ? 'danger' : 'primary'}`}>
                                                {latestAppointment.status}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="mt-3 mt-md-0">
                                        <Button variant="primary" onClick={() => navigate(`/patient/myAppointments/${latestAppointment.id}`)}>
                                            View Details
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Card className="dashboard-card">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="mb-0">Recent Medical Report</h4>
                            </div>
                            <hr />
                            {loading ? (
                                <div className="text-center mt-3"><Spinner animation="border" variant="primary" /></div>
                            ) : latestReport ? (
                                <div className="appointment-card">
                                    <p className="mb-1"><strong>{latestReport.patientName}</strong> <span className="text-muted">({latestReport.patientCode})</span></p>
                                    <p className="mb-1"><strong>Doctor:</strong> Dr. {latestReport.doctor}</p>
                                    <p className="mb-2"><strong>Date:</strong> {new Date(latestReport.date.seconds * 1000).toLocaleDateString()}</p>
                                    <a href={latestReport.fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary w-100">
                                        View Full Report
                                    </a>
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <FaCalendarTimes className="icon reports" />
                                    <h5>No reports found</h5>
                                    <p>You have no recent medical reports.</p>
                                    <Button variant="outline-primary" onClick={() => navigate('/patient/medical-reports')} className="mt-2">
                                        View History
                                    </Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card className="dashboard-card">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="mb-0">Active Prescription</h4>
                            </div>
                            <hr />
                            {loading ? (
                                <div className="text-center mt-3"><Spinner animation="border" variant="primary" /></div>
                            ) : latestPrescription ? (
                                <div className="appointment-card">
                                    <p className="mb-1"><strong>Doctor:</strong> Dr. {latestPrescription.doctor} <span className="text-muted">({latestPrescription.specialization})</span></p>
                                    <p className="mb-1"><strong>Date:</strong> {new Date(latestPrescription.date.seconds * 1000).toLocaleDateString()}</p>
                                    <div className="p-3 mt-2 bg-white rounded border">
                                        <strong>Medication:</strong>
                                        <p className="mb-0 mt-1">{latestPrescription.medication}</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <FaCalendarTimes className="icon prescriptions" />
                                    <h5>No prescriptions found</h5>
                                    <p>You have no active prescriptions.</p>
                                    <Button variant="outline-primary" onClick={() => navigate('/patient/prescriptions')} className="mt-2">
                                        View All
                                    </Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default PatientDashboard;
